import { fmtDateTime } from '../lib/dates.ts';
import { useEffect, useState } from 'react';
import { Attachments } from './Attachments.tsx';
import { AttachButton } from './AttachButton.tsx';
import { HandoffQR } from './HandoffQR.tsx';
import { thread, type ThreadEntry } from '../lib/api.ts';

interface Props {
  readonly requestId: string;
  readonly canReply: boolean;
  /** Devices can sign a reply; accounts are already named. */
  readonly canSign?: boolean;
  readonly onReplied: () => void;
}

/**
 * The conversation on one request.
 *
 * Bounded by a ticket rather than open-ended, which is what keeps it
 * from becoming the chat the product deliberately avoids. It closes
 * when the request closes.
 */
export const Thread = ({
  requestId,
  canReply,
  canSign,
  onReplied,
}: Props) => {
  const [entries, setEntries] = useState<ThreadEntry[]>([]);
  const [body, setBody] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const [qrFor, setQrFor] = useState('');
  const [error, setError] = useState('');

  const load = () => {
    thread.read(requestId).then(setEntries).catch(() => setEntries([]));
  };

  useEffect(load, [requestId]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    setError('');
    try {
      await thread.reply(requestId, text, pin.length >= 4 ? pin : undefined);
      setPin('');
      setBody('');
      load();
      onReplied();
    } catch {
      setError('Could not send. The ticket may have been closed.');
    } finally {
      setBusy(false);
    }
  };

  const when = (iso: string): string =>
    fmtDateTime(iso);

  return (
    <div className="thread">
      {entries.map((e) => (
        <div
          key={e.id}
          className={e.fromSite ? 'threadMsg site' : 'threadMsg hq'}
        >
          <div className="threadMeta">
            <span className="mono">{e.author}</span>
            <span className="mono">{when(e.createdAt)}</span>
          </div>
          <p>{e.body}</p>
          {/* A fault is easier to show than describe, and the reply
              is already a message, so the same attachment strip the
              board uses works here unchanged. */}
          <Attachments key={`${e.id}:${version}`} messageId={e.id} />

          <div className="threadAttach">
            <AttachButton
              messageId={e.id}
              onUploaded={() => setVersion((v) => v + 1)}
            />
            <button
              className="iconBtn"
              title="Photo from phone"
              aria-label="Photo from phone"
              onClick={() => setQrFor(qrFor === e.id ? '' : e.id)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" fill="none"
                   stroke="currentColor" strokeWidth="1.7"
                   strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="7" width="18" height="13" rx="2" />
                <circle cx="12" cy="13.5" r="3.2" />
              </svg>
            </button>
          </div>
          {qrFor === e.id && (
            <HandoffQR
              messageId={e.id}
              onClose={() => {
                setQrFor('');
                setVersion((v) => v + 1);
              }}
            />
          )}
        </div>
      ))}

      {error && <p className="err">{error}</p>}

      {canReply && (
        <div className="threadReply">
          <textarea
            rows={2}
            value={body}
            placeholder="Add to this ticket..."
            onChange={(e) => setBody(e.target.value)}
          />
          {canSign && (
            <input
              className="pinInput"
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              placeholder="PIN"
              title="Optional: puts your name on this reply"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            />
          )}
          <button
            className="primary"
            disabled={busy || !body.trim()}
            onClick={send}
          >
            {busy ? 'Sending...' : 'Reply'}
          </button>
        </div>
      )}
    </div>
  );
};
